import MetricComparisonChartTooltip from "./metric-comparison-chart-tooltip";
import { Box, BoxContent, BoxHeader, BoxHeading, BoxSubheading } from "@edgetier/client-components";
import {
    CartesianGrid,
    Label,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    ReferenceArea,
    Tooltip,
    XAxis,
    YAxis,
    ReferenceLine,
} from "recharts";
import {
    combineData,
    getMetricProperty,
    lowerXAxisTickInterval,
    upperXAxisTickInterval,
} from "./metric-comparison-chart.utilities";
import { FunctionComponent, memo, useContext, useMemo } from "react";
import { ICombineData, IProps } from "./metric-comparison-chart.types";
import { LINE_ANIMATION_MILLISECONDS } from "./metric-comparison-chart.constants";

import { Button, SpinnerUntil } from "@edgetier/components";
import { TimePeriodGroupBy } from "@edgetier/watchtower-types";
import { useAgentFirstName } from "hooks-for/agents/use-agent-first-name";
import { useInteractionMetricConversion } from "hooks-for/metrics/use-interaction-metric-conversion";
import { useInteractionMetricsSummaries } from "hooks-for/interaction-metrics/use-interaction-metrics-summaries";
import { useInteractionQueryParameters } from "hooks-for/filters/use-interaction-query-parameters";
import useChartDrag from "hooks-for/anomalies/use-chart-drag";
import MetricValuesChartDateRange from "pages/anomaly/anomaly-metric-values-chart/metric-values-chart-date-range";
import { isFilterSingleSelection } from "utilities/is-filter-single-selection";
import { faUndo } from "@fortawesome/pro-solid-svg-icons";
import { INTERACTIONS_TABLE_PREFIX } from "constants/interactions";
import { ExploreContext } from "components-for/explore/explore-context/explore-context";
import MaximumChartData, { MAXIMUM_DATA_POINTS } from "components-for/charts/maximum-chart-data";
import { dateTimeUpperXAxisTickFormatter } from "utilities-for/charts/date-time-upper-x-axis-tick-formatter";
import "./metric-comparison-chart.scss";
import DateTimeLowerAxisTickFormatter from "components-for/charts/date-time-lower-axis-tick-formatter";

/**
 * Chart an agent's metric values versus everyone else's.
 * @param props.defaultEndDateTime   Closing date in the range to search.
 * @param props.defaultStartDateTime Opening date in the range to search.
 * @param props.metric               Metric to chart.
 * @returns                          Chart.
 */
const MetricComparisonChart: FunctionComponent<IProps> = ({
    comparisonAgentId,
    defaultEndDateTime,
    defaultStartDateTime,
    height = "100%",
    metric,
    reviewAgentId,
    width = "100%",
}) => {
    const { chartGroupBy } = useContext(ExploreContext);
    const filters = useInteractionQueryParameters({
        defaults: { startDateTime: defaultStartDateTime, endDateTime: defaultEndDateTime },
    });

    // Data for just the selected agent.
    const agentParameters = { ...filters, agentId: reviewAgentId, groupBy: chartGroupBy };
    const { data: agentData, isLoading: isAgentDataLoading } = useInteractionMetricsSummaries(agentParameters);

    // Data for all agents.
    const overallParameters = { ...filters, agentId: comparisonAgentId, groupBy: chartGroupBy };
    const { data: overallData, isLoading: isOverallDataLoading } = useInteractionMetricsSummaries(overallParameters);

    // Combine agent and overall data into one array for the chart.
    const combinedData = useMemo(() => combineData(metric, agentData, overallData), [agentData, overallData, metric]);

    // Average interaction metrics data
    const { data: averageInteractionMetricsData, isLoading: isAverageInteractionMetricsDataLoading } =
        useInteractionMetricsSummaries(filters);
    const [averageMetricValue] = useMemo(
        () => (averageInteractionMetricsData ?? []).map((datum) => getMetricProperty(datum, metric)),
        [averageInteractionMetricsData, metric]
    );

    // Maybe convert seconds to something more appropriate (minutes most likely).
    const { axisLabel, formatTick, metricLabel, unitShorthand } = useInteractionMetricConversion(metric, {
        isAggregate: true,
        items: combinedData.length > MAXIMUM_DATA_POINTS ? [] : combinedData,
        getValue: ({ agentValue, overallValue }) => Math.max(agentValue ?? 0, overallValue ?? 0),
    });

    // Get the agent's name for labels etc.
    const { data: agentFirstName, isLoading: isAgentFirstNameLoading } = useAgentFirstName(reviewAgentId ?? "", {
        enabled: typeof reviewAgentId === "string",
    });

    const dateTime = useMemo(() => new Date(), []);

    // Chart drag hook
    const { filterDateRange, resetChartFilter, mouseEventCallbacks } = useChartDrag({
        defaultValues: {
            startDateTime: filters.startDateTime ?? dateTime,
            endDateTime: filters.endDateTime ?? dateTime,
        },
        timePeriodGroupBy: chartGroupBy,
        prefixOptions: {
            prefix: INTERACTIONS_TABLE_PREFIX,
            isPrefixOptional: false,
        },
    });

    // Custom label text
    const renderCustomizedLabel = (props: any) => {
        const {
            viewBox: { x, y, width },
            value,
            offset,
        } = props;

        // Based on font size
        const textHeight = 16;

        return (
            <text
                offset={offset}
                x={x + width - offset}
                y={y < textHeight ? y + textHeight : y - 8}
                className="recharts-text recharts-label"
                textAnchor="end"
            >
                <tspan x={x + width} dy="0em">
                    {value}
                </tspan>
            </text>
        );
    };

    // Use a single line for showing the selected area when a click is made on chart
    const isSingleSelection = isFilterSingleSelection(filterDateRange);

    return (
        <Box className="metric-comparison-chart">
            <BoxHeader>
                <BoxHeading>{metricLabel} Comparison</BoxHeading>
                {typeof reviewAgentId === "string" && (
                    <BoxSubheading>
                        {isAgentFirstNameLoading && <>&hellip;</>}
                        {!isAgentFirstNameLoading && (
                            <>
                                {agentFirstName}'s {metricLabel.toLowerCase()} versus the comparison agents'
                            </>
                        )}
                    </BoxSubheading>
                )}
            </BoxHeader>
            <BoxContent>
                <SpinnerUntil<{
                    agentFirstName: string;
                    combinedData: ICombineData[];
                    averageMetricValue: number | null;
                }>
                    data={{ agentFirstName, combinedData, averageMetricValue }}
                    isReady={!isAgentDataLoading && !isOverallDataLoading && !isAverageInteractionMetricsDataLoading}
                >
                    {(data) => (
                        <MaximumChartData dataLength={data.combinedData.length}>
                            <div className="metric-comparison-chart__filter-info">
                                <MetricValuesChartDateRange
                                    firstDate={filterDateRange[0].date}
                                    secondDate={filterDateRange[1].date}
                                    timePeriodGroup={chartGroupBy}
                                />
                                <Button
                                    disabled={filterDateRange.some(({ activeLabel }) => activeLabel === null)}
                                    icon={faUndo}
                                    onClick={() => resetChartFilter()}
                                    isIconOnly
                                />
                            </div>
                            <ResponsiveContainer height={height} width={width}>
                                <LineChart
                                    data={data.combinedData}
                                    margin={{ left: 10, top: 5, right: 5 }}
                                    {...mouseEventCallbacks}
                                >
                                    <XAxis
                                        xAxisId={0}
                                        dataKey="dateTime"
                                        interval={upperXAxisTickInterval(chartGroupBy)}
                                        tickFormatter={(tickValue) => {
                                            return dateTimeUpperXAxisTickFormatter(
                                                filters.startDateTime ?? defaultStartDateTime,
                                                tickValue,
                                                chartGroupBy
                                            );
                                        }}
                                        tick={{
                                            fontSize:
                                                chartGroupBy === TimePeriodGroupBy.Minutes ||
                                                chartGroupBy === TimePeriodGroupBy.Hours
                                                    ? 10
                                                    : undefined,
                                        }}
                                    />
                                    {chartGroupBy !== TimePeriodGroupBy.Months && (
                                        <XAxis
                                            xAxisId={1}
                                            axisLine={false}
                                            dataKey="dateTime"
                                            scale="band"
                                            interval={lowerXAxisTickInterval(chartGroupBy)}
                                            tick={(tickValue) => (
                                                <DateTimeLowerAxisTickFormatter
                                                    tickValue={tickValue}
                                                    dataLength={data.combinedData.length}
                                                    timePeriodGroup={chartGroupBy}
                                                />
                                            )}
                                        />
                                    )}
                                    <YAxis tickFormatter={(value) => formatTick(value)}>
                                        <Label
                                            angle={-90}
                                            offset={5}
                                            value={axisLabel}
                                            position="insideLeft"
                                            style={{ textAnchor: "middle" }}
                                        />
                                    </YAxis>
                                    <CartesianGrid />

                                    {typeof reviewAgentId === "string" && (
                                        <Line
                                            animationDuration={LINE_ANIMATION_MILLISECONDS}
                                            dataKey="agentValue"
                                            dot
                                            name={data.agentFirstName}
                                            stroke="#16a085"
                                            strokeWidth="2"
                                            type="monotoneX"
                                        />
                                    )}

                                    <Line
                                        animationDuration={LINE_ANIMATION_MILLISECONDS}
                                        dataKey="overallValue"
                                        dot
                                        name={typeof reviewAgentId === "string" ? "Others" : "All Agents"}
                                        stroke="#2980b9"
                                        strokeWidth="2"
                                        type="monotoneX"
                                    />
                                    {isSingleSelection ? (
                                        <ReferenceLine
                                            isFront
                                            strokeOpacity={1}
                                            stroke={"#1abc9c"}
                                            strokeWidth={2}
                                            x={filterDateRange[0].activeLabel ?? ""}
                                        />
                                    ) : (
                                        <ReferenceArea
                                            fill={"#1abc9c"}
                                            fillOpacity={0.3}
                                            x1={filterDateRange[0].activeLabel ?? ""}
                                            x2={filterDateRange[1].activeLabel ?? ""}
                                        />
                                    )}

                                    <ReferenceLine
                                        y={data.averageMetricValue ?? 0}
                                        stroke="#64748b"
                                        strokeDasharray="6 4"
                                        strokeWidth="2"
                                    >
                                        <Label
                                            value={`Overall (${formatTick(data.averageMetricValue ?? 0, {
                                                minimumFractionDigits: 1,
                                                maximumFractionDigits: 2,
                                            })})`}
                                            content={renderCustomizedLabel}
                                        />
                                    </ReferenceLine>
                                    <Legend />
                                    <Tooltip
                                        isAnimationActive={false}
                                        content={({ active, payload }) =>
                                            active &&
                                            Array.isArray(payload) &&
                                            payload.length > 0 &&
                                            typeof payload[0].value === "number" && (
                                                <MetricComparisonChartTooltip
                                                    agentFirstName={data.agentFirstName}
                                                    agentValueString={formatTick(payload[0].payload.agentValue, {
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                    dateTime={payload[0].payload.dateTime}
                                                    overallValueString={formatTick(payload[0].payload.overallValue, {
                                                        minimumFractionDigits: 1,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                    timePeriodGroup={chartGroupBy}
                                                    unitShorthand={unitShorthand}
                                                />
                                            )
                                        }
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </MaximumChartData>
                    )}
                </SpinnerUntil>
            </BoxContent>
        </Box>
    );
};

export default memo(MetricComparisonChart);
